import { useEffect, useRef, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { Camera, ImagePlus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { AgencyLogoAvatar } from '@modules/_shared/components/AgencyLogoAvatar';
import { CoverPhoto } from '@modules/_shared/components/CoverPhoto';
import { AgencyImageUploadModal } from '@modules/_shared/components/image-upload/implementations/AgencyImageUploadModal';
import {
  AgentImageUploadModal,
  SET_PRIMARY_IMAGE,
  updateCacheForPrimaryImage,
} from '@modules/_shared/components/image-upload/implementations/AgentImageUploadModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { AgencyProfileBadges } from '@modules/_shared/components/visibility-drawer/edit-modals/AgencyProfileBadges';
import { AgentProfileBadges } from '@modules/_shared/components/visibility-drawer/edit-modals/AgentProfileBadges';
import { GET_TEAM_MEMBER_EDIT_DETAILS } from '@modules/_shared/components/visibility-drawer/queries/agent-queries';
import { PencilEditIcon } from '@modules/_shared/components/visibility-drawer/sections/EditableSectionHeader';
import type {
  SectionProps,
  VisibilityEntity,
} from '@modules/_shared/components/visibility-drawer/types';
import {
  isAgencyEntity,
  isAgentEntity,
  resolveTeamPhone,
} from '@modules/_shared/components/visibility-drawer/types';
import { useDrawerContext } from '@modules/_shared/components/visibility-drawer/VisibilityDrawer';
import { useLocale } from '@shared/hooks/locale';
import { customPalette, rawPalette } from '@shared/theme/StyleProvider';

const GET_USER_IMAGE_FOR_ENHANCED_ACTION = gql(/* GraphQL */ `
  query GetUserImageForEnhancedAction($id: uuid!) {
    user_images_by_pk(id: $id) {
      id
      is_primary
      user_id
      image {
        id
        ai_enhanced_at
      }
    }
  }
`);

const DELETE_NON_PRIMARY_USER_IMAGE = gql(/* GraphQL */ `
  mutation DeleteNonPrimaryUserImage($id: uuid!, $userId: uuid!) {
    delete_user_images(
      where: {
        id: { _eq: $id }
        user_id: { _eq: $userId }
        is_primary: { _eq: false }
      }
    ) {
      returning {
        id
      }
    }
  }
`);

const ENHANCED_PHOTO_ACTION_PARAM = 'enhanced_photo_action';
const ENHANCED_PHOTO_IMAGE_PARAM = 'user_image_id';

class PhotoActionError extends Error {
  constructor(message: string, readonly options: { report?: boolean } = {}) {
    super(message);
  }
}

const BANNER_HEIGHT = { xs: 140, sm: 180 };

const getEntityProfileImage = (entity: VisibilityEntity) => {
  if (isAgentEntity(entity)) {
    return entity.user_images?.[0]?.image.url ?? undefined;
  }
  if (isAgencyEntity(entity)) {
    return entity.logo?.url ?? undefined;
  }
  return undefined;
};

const getEntityBannerImage = (entity: VisibilityEntity) => {
  if (isAgentEntity(entity)) {
    return entity.teams_users?.[0]?.team?.cover_photo?.url ?? undefined;
  }
  if (isAgencyEntity(entity)) {
    return entity.cover_photo?.url ?? undefined;
  }
  return undefined;
};

const getEntityDisplayName = (entity: VisibilityEntity) => {
  if (isAgentEntity(entity)) {
    return [entity.first_name, entity.last_name].filter(Boolean).join(' ');
  }
  if (isAgencyEntity(entity)) {
    return entity.name;
  }
  return '';
};

const BannerSection = ({ entity }: SectionProps) => {
  const { t } = useLocale();
  const { setOpenModal, contextId } = useDrawerContext();
  const profileImage = getEntityProfileImage(entity);
  const bannerImage = getEntityBannerImage(entity);
  const [profileUploaderOpen, setProfileUploaderOpen] = useState(false);
  const [bannerUploaderOpen, setBannerUploaderOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [autoOpenEnhance, setAutoOpenEnhance] = useState(false);
  const [actionFeedback, setActionFeedback] = useState<{
    message: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);
  const handledActionRef = useRef(false);

  const userId = isAgentEntity(entity) ? entity.id : null;

  const [setPrimaryImage] = useMutation(SET_PRIMARY_IMAGE, {
    // The banner reads the primary photo from GET_TEAM_MEMBER_EDIT_DETAILS,
    // whose `user_images(where: is_primary)` filter Apollo can't re-evaluate
    // from cache. updateCacheForPrimaryImage rewrites that field directly, but
    // we also refetch the entity query (as PublishMenu does) so the banner is
    // guaranteed fresh after an email-link accept.
    refetchQueries: [GET_TEAM_MEMBER_EDIT_DETAILS],
    update: (cache, { data }) => {
      if (!data?.update_user_images_by_pk || !userId) {
        return;
      }
      updateCacheForPrimaryImage(cache, userId, data.update_user_images_by_pk);
    },
  });
  const [deleteUserImage] = useMutation(DELETE_NON_PRIMARY_USER_IMAGE, {
    update: (cache, { data }) => {
      const deletedImage = data?.delete_user_images?.returning[0];
      if (!deletedImage) {
        return;
      }
      cache.evict({
        id: cache.identify({
          __typename: 'user_images',
          id: deletedImage.id,
        }),
      });
    },
  });
  const [loadUserImage] = useLazyQuery(GET_USER_IMAGE_FOR_ENHANCED_ACTION);

  useEffect(() => {
    const action = searchParams.get(ENHANCED_PHOTO_ACTION_PARAM);
    const userImageId = searchParams.get(ENHANCED_PHOTO_IMAGE_PARAM);

    if (!action || !userImageId || !userId || handledActionRef.current) {
      return;
    }
    handledActionRef.current = true;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(ENHANCED_PHOTO_ACTION_PARAM);
        next.delete(ENHANCED_PHOTO_IMAGE_PARAM);
        return next;
      },
      { replace: true },
    );

    const run = async () => {
      try {
        if (action !== 'accept' && action !== 'reject' && action !== 'edit') {
          throw new PhotoActionError(t('Could not apply the photo action'));
        }

        // Always resolve the image first and confirm it belongs to this agent.
        // userImageId comes from the email URL, so we never act on a row that
        // isn't this user's — even if Hasura permissions would also allow it.
        const { data } = await loadUserImage({
          variables: { id: userImageId },
        });
        const userImage = data?.user_images_by_pk;
        // Accepting only changes which of this agent's existing images is
        // primary. Keep the AI-only restriction for the other email actions so
        // a crafted reject link cannot delete a regular profile photo.
        if (
          !userImage ||
          userImage.user_id !== userId ||
          (action !== 'accept' && !userImage.image.ai_enhanced_at)
        ) {
          throw new PhotoActionError(
            t('The suggested photo could not be found'),
          );
        }

        if (action === 'accept') {
          setActionFeedback({
            message: t('Applying your new profile photo…'),
            severity: 'info',
          });
          const { data: result } = await setPrimaryImage({
            variables: { userId, userImageId },
          });
          if (!result?.update_user_images_by_pk) {
            throw new PhotoActionError(t('Could not apply the photo action'), {
              report: true,
            });
          }
          setActionFeedback({
            message: t('Your new profile photo has been applied'),
            severity: 'success',
          });
        } else if (action === 'reject') {
          if (userImage.is_primary) {
            throw new PhotoActionError(t('Could not apply the photo action'));
          }
          setActionFeedback({
            message: t('Removing the suggested photo…'),
            severity: 'info',
          });
          const { data: result } = await deleteUserImage({
            variables: { id: userImageId, userId },
          });
          if (!result?.delete_user_images?.returning[0]) {
            throw new PhotoActionError(t('Could not apply the photo action'), {
              report: true,
            });
          }
          setActionFeedback({
            message: t('The suggested photo has been deleted'),
            severity: 'success',
          });
        } else {
          if (!profileImage) {
            throw new PhotoActionError(t('Could not apply the photo action'));
          }
          // The email row has no source-image link, so edit the current primary.
          setAutoOpenEnhance(true);
          setProfileUploaderOpen(true);
        }
      } catch (error) {
        if (!(error instanceof PhotoActionError) || error.options.report) {
          captureException(error, {
            tags: { feature: 'enhanced-profile-photo-action' },
          });
        }
        setActionFeedback({
          message:
            error instanceof PhotoActionError
              ? error.message
              : t('Could not apply the photo action'),
          severity: 'error',
        });
      }
    };
    void run();
  }, [
    searchParams,
    setSearchParams,
    userId,
    setPrimaryImage,
    deleteUserImage,
    loadUserImage,
    profileImage,
    t,
  ]);

  const renderProfileBadges = () => {
    if (isAgentEntity(entity)) {
      const personalPhone =
        entity.phone_numbers?.find(pn => pn.is_visibility)?.number ?? null;
      const displayPhone =
        personalPhone ?? resolveTeamPhone(entity, contextId)?.number ?? null;
      return (
        <AgentProfileBadges
          hiresOnRealAdvisor={entity.hires_on_realadvisor}
          activeSince={entity.active_since}
          languages={entity.languages}
          phone={displayPhone}
          onEditLanguages={() => setOpenModal('languages')}
          onEditHiresOnRealAdvisor={() => setOpenModal('hiresOnRealAdvisor')}
          onEditYearsInBusiness={() => setOpenModal('yearsInBusiness')}
          onEditPhone={() => setOpenModal('agentPhone')}
        />
      );
    }

    if (isAgencyEntity(entity)) {
      return (
        <AgencyProfileBadges
          address={{
            street_number: entity.street_number,
            route: entity.route,
            postcode: entity.postcode,
            locality: entity.locality,
            state: entity.state,
            country: entity.country,
          }}
          website={entity.website_url}
          activeSince={entity.active_since}
          phone={entity.phone_numbers?.[0]?.number ?? null}
          onEditAddress={() => setOpenModal('teamAddress')}
          onEditWebsite={() => setOpenModal('teamWebsite')}
          onEditYearsInBusiness={() => setOpenModal('yearsInBusiness')}
          onEditPhone={() => setOpenModal('teamPhone')}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'grey.200',
          mb: 2,
        }}
      >
        {/* Cover photo */}
        <CoverPhoto
          src={bannerImage}
          width={1400}
          sx={{ height: BANNER_HEIGHT, position: 'relative' }}
        >
          {isAgencyEntity(entity) && (
            <Tooltip title={t('Change cover photo')}>
              <IconButton
                aria-label="Edit banner"
                onClick={() => setBannerUploaderOpen(true)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: 34,
                  height: 34,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                  },
                }}
              >
                <ImagePlus size={16} />
              </IconButton>
            </Tooltip>
          )}
        </CoverPhoto>

        {/* Avatar + Name row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            px: 2,
            mt: -8,
            position: 'relative',
          }}
        >
          {/* Avatar with edit button */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            {isAgencyEntity(entity) ? (
              <AgencyLogoAvatar
                logoUrl={profileImage}
                sx={{
                  width: 100,
                  height: 100,
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.12)',
                  borderRadius: '12px',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  '& img': {
                    backgroundColor: rawPalette.white,
                    objectFit: 'contain',
                    borderRadius: '8px',
                  },
                }}
                src={profileImage ?? undefined}
                fallbackSx={{ color: customPalette.gray400 }}
              />
            ) : (
              <Avatar
                src={profileImage ?? undefined}
                variant="circular"
                sx={{
                  width: 100,
                  height: 100,
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.12)',
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  p: 0,
                  '& img': {
                    objectFit: 'cover',
                    borderRadius: '50%',
                  },
                }}
              />
            )}

            <Tooltip
              title={
                isAgencyEntity(entity)
                  ? t('Change logo')
                  : t('Change profile photo')
              }
            >
              <IconButton
                size="small"
                aria-label="Edit profile picture"
                onClick={() => setProfileUploaderOpen(true)}
                sx={{
                  position: 'absolute',
                  bottom: isAgencyEntity(entity) ? -4 : 4,
                  right: isAgencyEntity(entity) ? -4 : 4,
                  width: 30,
                  height: 30,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                  },
                }}
              >
                <Camera size={14} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Name + edit button */}
          <Box
            component="button"
            type="button"
            onClick={() => {
              if (isAgencyEntity(entity)) {
                setOpenModal('agencyName');
              } else {
                setOpenModal('agentName');
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              border: 'none',
              background: 'transparent',
              p: 0,
              textAlign: 'left',
              outline: 'none',
              borderRadius: '6px',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              },
              '&:hover .edit-icon, &:focus-visible .edit-icon': {
                opacity: 1,
              },
            }}
          >
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
              }}
            >
              {getEntityDisplayName(entity)}
            </Typography>

            <PencilEditIcon size={18} sx={{ flexShrink: 0 }} />
          </Box>
        </Box>

        {/* Badges row — full width below */}
        <Box sx={{ p: 2 }}>{renderProfileBadges()}</Box>
      </Paper>

      {/* Image upload modals */}
      {isAgencyEntity(entity) ? (
        <>
          <AgencyImageUploadModal
            teamId={entity.id}
            imageType="logo"
            currentImageUrl={profileImage}
            open={profileUploaderOpen}
            onClose={() => setProfileUploaderOpen(false)}
          />
          <AgencyImageUploadModal
            teamId={entity.id}
            teamName={entity.name}
            logoUrl={profileImage}
            imageType="banner"
            currentImageUrl={bannerImage}
            open={bannerUploaderOpen}
            onClose={() => setBannerUploaderOpen(false)}
          />
        </>
      ) : (
        <AgentImageUploadModal
          userId={entity.id}
          agentName={getEntityDisplayName(entity)}
          currentImageUrl={profileImage}
          open={profileUploaderOpen}
          onClose={() => {
            setProfileUploaderOpen(false);
            setAutoOpenEnhance(false);
          }}
          autoOpenEnhance={autoOpenEnhance}
        />
      )}

      {actionFeedback && (
        <RASnackbar
          key={actionFeedback.severity === 'info' ? 'loading' : 'result'}
          message={actionFeedback.message}
          severity={actionFeedback.severity}
          onClose={() => setActionFeedback(null)}
        />
      )}
    </>
  );
};

export default BannerSection;
